import React from 'react';
import { Drawer, Card, Typography, Space, Timeline, Tag, Progress, Statistic, Collapse } from 'antd';
import { 
  ClockCircleOutlined, 
  CheckCircleOutlined, 
  ExclamationCircleOutlined,
  BugOutlined,
  RocketOutlined
} from '@ant-design/icons';

const { Text, Paragraph } = Typography;
const { Panel } = Collapse;

interface ExecutionPanelProps {
  visible: boolean;
  onClose: () => void;
  results: any;
  history: any[];
}

const ExecutionPanel: React.FC<ExecutionPanelProps> = ({ 
  visible, 
  onClose, 
  results, 
  history 
}) => {
  if (!results) {
    return (
      <Drawer
        title="Execution Results"
        placement="right"
        width={500}
        onClose={onClose}
        open={visible}
      >
        <div className="flex items-center justify-center h-64">
          <Text type="secondary">No execution results yet</Text>
        </div>
      </Drawer>
    );
  }

  const { result } = results;

  const getStepIcon = (_step: string, index: number, steps: string[]) => {
    if (index < steps.length - 1) {
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    }
    return <ClockCircleOutlined style={{ color: '#1890ff' }} />;
  };

  const getStepColor = (step: string) => {
    const colorMap: { [key: string]: string } = {
      'start': 'green',
      'enhance_query': 'blue',
      'search_documents': 'orange',
      'select_context': 'purple',
      'generate_response': 'cyan',
      'evaluate_response': 'magenta',
      'decide_next': 'gold',
      'refine_response': 'volcano',
      'end': 'red'
    };
    return colorMap[step] || 'default';
  };

  const formatStepName = (step: string) => {
    return step.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <Drawer
      title="Execution Results"
      placement="right"
      width={600}
      onClose={onClose}
      open={visible}
      styles={{ body: { padding: '16px' } }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Execution Summary */}
        <Card title="Execution Summary" size="small">
          <div className="grid grid-cols-2 gap-4">
            <Statistic
              title="Status"
              value={results.status}
              prefix={
                results.status === 'success' ? 
                  <CheckCircleOutlined style={{ color: '#52c41a' }} /> :
                  <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
              }
              valueStyle={{ 
                color: results.status === 'success' ? '#52c41a' : '#ff4d4f' 
              }}
            />
            <Statistic
              title="Total Time"
              value={result?.timings?.total || 0}
              suffix="ms"
              prefix={<ClockCircleOutlined />}
            />
            <Statistic
              title="Confidence"
              value={result?.confidence ? (result.confidence * 100).toFixed(1) : 0}
              suffix="%"
              prefix={<RocketOutlined />}
            />
            <Statistic
              title="Steps Completed"
              value={result?.processingSteps?.length || 0}
              prefix={<CheckCircleOutlined />}
            />
          </div>
        </Card>

        {/* Processing Timeline */}
        {result?.processingSteps && (
          <Card title="Processing Timeline" size="small">
            <Timeline>
              {result.processingSteps.map((step: string, index: number) => (
                <Timeline.Item
                  key={step}
                  dot={getStepIcon(step, index, result.processingSteps)}
                  color={index < result.processingSteps.length - 1 ? 'green' : 'blue'}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <Text strong>{formatStepName(step)}</Text>
                      <br />
                      <Tag color={getStepColor(step)}>
                        {step}
                      </Tag>
                    </div>
                    {result.timings?.[step] && (
                      <Text type="secondary">
                        {result.timings[step]}ms
                      </Text>
                    )}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>
        )}

        {/* Performance Breakdown */}
        {result?.timings && (
          <Card title="Performance Breakdown" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              {Object.entries(result.timings)
                .filter(([key]) => key !== 'total')
                .map(([stepName, time]: [string, any]) => (
                  <div key={stepName}>
                    <div className="flex justify-between mb-1">
                      <Text>{formatStepName(stepName)}</Text>
                      <Text>{time}ms</Text>
                    </div>
                    <Progress
                      percent={((time / result.timings.total) * 100)}
                      size="small"
                      strokeColor={getStepColor(stepName)}
                      showInfo={false}
                    />
                  </div>
                ))}
            </Space>
          </Card>
        )}

        {/* Tool and LLM Calls */}
        <div className="grid grid-cols-2 gap-4">
          <Card title="Tool Calls" size="small">
            <Statistic
              value={result?.toolCalls || 0}
              prefix={<BugOutlined />}
            />
          </Card>
          <Card title="LLM Calls" size="small">
            <Statistic
              value={result?.llmCalls || 0}
              prefix={<RocketOutlined />}
            />
          </Card>
        </div>

        {/* Response Details */}
        {result?.response && (
          <Card title="Generated Response" size="small">
            <Collapse>
              <Panel header="View Response" key="response">
                <Paragraph>
                  <Text>{result.response}</Text>
                </Paragraph>
              </Panel>
            </Collapse>
          </Card>
        )}

        {/* Errors and Warnings */}
        {(result?.errors?.length > 0 || result?.warnings?.length > 0) && (
          <Card title="Issues" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              {result.errors?.map((error: string, index: number) => (
                <div key={index} className="p-2 bg-red-50 border border-red-200 rounded">
                  <Text type="danger">
                    <ExclamationCircleOutlined /> {error}
                  </Text>
                </div>
              ))}
              {result.warnings?.map((warning: string, index: number) => (
                <div key={index} className="p-2 bg-yellow-50 border border-yellow-200 rounded">
                  <Text type="warning">
                    <ExclamationCircleOutlined /> {warning}
                  </Text>
                </div>
              ))}
            </Space>
          </Card>
        )}

        {/* Execution History */}
        {history.length > 1 && (
          <Card title="Execution History" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              {history.slice(-5).map((execution, index) => (
                <div 
                  key={index} 
                  className="p-3 border border-gray-200 rounded hover:bg-gray-50 cursor-pointer"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <Text strong>
                        {execution.timestamp.toLocaleTimeString()}
                      </Text>
                      <br />
                      <Text type="secondary">
                        {execution.result.status} • {execution.result.result?.timings?.total || 0}ms
                      </Text>
                    </div>
                    <Tag color={execution.result.status === 'success' ? 'green' : 'red'}>
                      {execution.result.status}
                    </Tag>
                  </div>
                </div>
              ))}
            </Space>
          </Card>
        )}
      </Space>
    </Drawer>
  );
};

export default ExecutionPanel;